import {LanguageType} from './language.type';

export type ICompanyManifest = {
	readonly companyId: string;
	readonly companyName: string;
	readonly logo: ICompanyManifestLogo;
	readonly landingPages: Record<string, ICompanyManifestDynamicLandingPage>;
};

// ============== LOGO ==============
export type ICompanyManifestLogo = {
	readonly url: string;
	readonly alt: string;
	readonly styles: Record<string, string>;
};

// ============== DYNAMIC LANDING PAGE ==============
export type ICompanyManifestDynamicLandingPage = {
	readonly pageId?: string; // e.g. 'signed-thank-you'
	readonly showLogo: boolean;
	readonly mainTitle: ILandingPageTitleConfig;
	readonly subTitle?: ILandingPageTitleConfig;
	readonly backgroundImage?: string;
	readonly ctaButton?: ILandingPageCTAConfig;
	readonly contactInfo?: ILandingPageContactConfig;
	readonly theme?: 'light' | 'dark' | 'auto';
};

export type ILandingPageTitleConfig = Partial<{
	[lang in LanguageType | 'default']: string;
}>;

export type ILandingPageCTAConfig = {
	readonly text: ILandingPageTitleConfig;
	readonly url: string;
	readonly style?: 'primary' | 'secondary' | 'outline';
	readonly target?: '_blank' | '_self';
};

export type ILandingPageContactConfig = {
	readonly email?: string;
	readonly phone?: string;
	readonly address?: ILandingPageTitleConfig;
	readonly website?: string;
};

// ============== FACTORY ==============
export function companyManifestFactory(manifest: Partial<ICompanyManifest>): ICompanyManifest {
	if (!manifest || !manifest.companyId) {
		throw new Error('Invalid company manifest data');
	}
	const logo: ICompanyManifestLogo = manifest?.logo
		? {
				url: manifest.logo?.url || '',
				alt: manifest.logo?.alt || `${manifest.companyName} logo`,
				styles: manifest.logo?.styles || {},
			}
		: undefined;

	const landingPages: Record<string, ICompanyManifestDynamicLandingPage> = manifest?.landingPages
		? Object.entries(manifest.landingPages).reduce(
				(acc, [key, value]) => {
					acc[key] = {
						...value,
						pageId: value?.pageId || key,
					};
					return acc;
				},
				{} as Record<string, ICompanyManifestDynamicLandingPage>,
			)
		: {};

	return {
		companyId: manifest.companyId,
		companyName: manifest?.companyName || manifest.companyId,
		logo,
		landingPages,
	};
}
